// Scheduled as a week (docs/56, round 3, alternative A), as the kit's WeekPlan.swift has it: the days from a date in
// columns, each with what the Scheduled list shows for it (Today's column is the Today list), "Unscheduled" under
// them, and how a to-do moves between days. The kit is the reference; cases/week.json is read by both.
//
// Nothing stored is new: a drop or a day later sets the start as the When picker does. The functions give the changed
// to-dos; the screen saves them with `updateTodos` as one change.

import { addDays, dayOf, maxDay } from "./days";
import type { Lists, ListSection, Route } from "./lists";
import type { Birthday, Project, Todo } from "./records";
import type { Day, Start } from "./types";

/** The days the week holds; those that don't fit the width are reached by scrolling. */
export const WEEK_DAYS = 7;
/** A column is never narrower than this; as many as fit are shown. */
export const MIN_COLUMN_WIDTH = 240;
/** A long day shows this many rows, then "Show N More". */
export const WEEK_ROW_LIMIT = 8;

/** The tabs of "Unscheduled": where a to-do without a day waits. */
export type WeekBand = "inbox" | "anytime" | "someday";
export const WEEK_BANDS: readonly WeekBand[] = ["inbox", "anytime", "someday"];

export function bandRoute(tab: WeekBand): Route {
  return { kind: tab };
}

/** One day of the week (WeekDay). */
export interface WeekDay {
  day: Day;
  /** Today's column is the Today list: overdue to-dos, what starts or is due today. */
  isToday: boolean;
  todos: Todo[];
  /** Projects that wait for this day, as Scheduled lists them; none in Today's column. */
  projects: Project[];
  birthdays: Birthday[];
}

/** How many columns fit beside each other: one at least, the week at most. */
export function visibleColumns(width: number, spacing = 0): number {
  const fitting = Math.floor((width + spacing) / (MIN_COLUMN_WIDTH + spacing));
  return Math.min(Math.max(fitting, 1), WEEK_DAYS);
}

/** The width of a column when the fitting ones share the width. */
export function columnWidth(width: number, spacing = 0): number {
  const count = visibleColumns(width, spacing);
  return Math.max(MIN_COLUMN_WIDTH, (width - spacing * (count - 1)) / count);
}

/** The first day after paging by `days` (‹ › a day, « » a week); never before today. */
export function paged(start: Day, days: number, today: Day): Day {
  return maxDay(addDays(start, days), today);
}

/**
 * The start after a day later (`days` > 0) or earlier (< 0): from the to-do's day, or from today when it is overdue,
 * never before today. Without a day it starts tomorrow on a day later and stays on a day earlier. Null: no change.
 */
export function movedStart(todo: Pick<Todo, "start">, days: number, today: Day): Start | null {
  if (days === 0) return null;
  let base: Day;
  if (todo.start.kind === "on") {
    base = maxDay(todo.start.day, today);
  } else {
    if (days < 0) return null;
    base = today;
  }
  const moved = maxDay(addDays(base, days), today);
  if (todo.start.kind === "on" && todo.start.day === moved) return null;
  return { kind: "on", day: moved };
}

/** The start a drop on a day gives: that day, or today for one that has passed. */
export function startDroppedOn(day: Day, today: Day): Start {
  return { kind: "on", day: maxDay(day, today) };
}

/**
 * The to-do with another start, as the When picker sets it (Todo.starting): the reminder keeps its clock time (it
 * rides the day), a repeat moves this time only, and a plan for a free time of another day is let go.
 */
export function starting(todo: Todo, start: Start, zone: string): Todo {
  const next: Todo = { ...todo, start };
  const keepsPlan = start.kind === "on" && todo.plannedUntil !== undefined && dayOf(todo.plannedUntil, zone) === start.day;
  if (!keepsPlan) {
    delete next.plannedUntil;
    delete next.planOrder;
  }
  return next;
}

/** The days from `from` (never before today), each with what Scheduled shows for it (Store.week(from:days:)). */
export function weekDays(lists: Lists, from: Day, count: number = WEEK_DAYS): WeekDay[] {
  const today = lists.today;
  const first = maxDay(from, today);
  const byDay = new Map<Day, Todo[]>();
  for (const todo of lists.scheduled()) {
    if (todo.start.kind === "on") byDay.set(todo.start.day, [...(byDay.get(todo.start.day) ?? []), todo]);
  }
  const projectsByDay = new Map<Day, Project[]>();
  for (const project of lists.scheduledProjects) {
    if (project.start?.kind === "on") projectsByDay.set(project.start.day, [...(projectsByDay.get(project.start.day) ?? []), project]);
  }
  return Array.from({ length: Math.max(count, 0) }, (_, offset) => {
    const day = addDays(first, offset);
    const isToday = day === today;
    return {
      day, isToday,
      todos: isToday ? lists.sections({ kind: "today" }).flatMap((section) => section.todos) : byDay.get(day) ?? [],
      projects: isToday ? [] : projectsByDay.get(day) ?? [],
      birthdays: lists.birthdaysOn(day),
    };
  });
}

/**
 * What a tab of "Unscheduled" shows (Store.band): the Inbox in one block, Anytime by area and project with only the
 * to-dos that have no day (today's are in Today's column), Someday as its list.
 */
export function band(lists: Lists, tab: WeekBand): ListSection[] {
  switch (tab) {
    case "inbox": return lists.sections({ kind: "inbox" });
    case "someday": return lists.sections({ kind: "someday" });
    case "anytime":
      return lists.sections({ kind: "anytime" }).flatMap((section) => {
        const todos = section.todos.filter((todo) => todo.start.kind === "anytime" && !lists.isToday(todo));
        return todos.length === 0 ? [] : [{ ...section, todos }];
      });
  }
}

/** A tab narrowed by the band's filter (Store.band(_:filter:)): the to-dos whose title has every word typed, in
 * groups that keep at least one. An empty filter narrows nothing. */
export function bandFiltered(lists: Lists, tab: WeekBand, filter: string): ListSection[] {
  const words = filter.toLocaleLowerCase().split(" ").filter((word) => word !== "");
  if (words.length === 0) return band(lists, tab);
  return band(lists, tab).flatMap((section) => {
    const todos = section.todos.filter((todo) => words.every((word) => todo.title.toLocaleLowerCase().includes(word)));
    return todos.length === 0 ? [] : [{ ...section, todos }];
  });
}

/**
 * The group of a tab that a to-do dropped on the tab shows in, by its id (WeekPlan.bandSectionID): the Inbox's one
 * group, else its project's, its area's when it has no project, or the one of to-dos with neither (`droppedOnBand`
 * keeps project and area outside the Inbox). Only that group marks the drop (Bastian, 2026-10-20: every group took it
 * as if the to-do would land there). Undefined when the tab has no such group yet, or the filter hides it.
 */
export function bandSectionID(todo: Todo, tab: WeekBand, sections: readonly ListSection[]): string | undefined {
  if (tab === "inbox") return sections[0]?.id;
  return sections.find((section) => {
    const header = section.header;
    switch (header.kind) {
      case "project": return todo.projectID === header.project.id;
      case "area": return todo.projectID === undefined && todo.areaID === header.area.id;
      case "general": return todo.projectID === undefined && todo.areaID === undefined;
      default: return false;
    }
  })?.id;
}

/** The open to-dos a tab holds, for its count. */
export function bandCount(lists: Lists, tab: WeekBand): number {
  return band(lists, tab).flatMap((section) => section.todos).filter((todo) => todo.completedAt === undefined).length;
}

/** ⌥⌘→ / ⌥⌘← in every list (Store.moveStart): the to-dos that change, a day later or earlier. */
export function movedByDays(todos: readonly Todo[], days: number, today: Day, zone: string): Todo[] {
  return todos.flatMap((todo) => {
    const start = movedStart(todo, days, today);
    return start === null ? [] : [starting(todo, start, zone)];
  });
}

/**
 * A drop on a day (Store.drop(_:onDay:before:)): the to-dos start that day (today for one that has passed) and stand
 * before `before` in it, or at its end. `all` is every to-do of the account, for the orders.
 */
export function droppedOnDay(lists: Lists, all: readonly Todo[], moving: readonly Todo[], day: Day, before: string | undefined, zone: string): Todo[] {
  const start = startDroppedOn(day, lists.today);
  if (start.kind !== "on") return [];
  const ids = new Set(moving.map((todo) => todo.id));
  const column = (weekDays(lists, start.day, 1)[0]?.todos ?? []).filter((todo) => !ids.has(todo.id));
  return placed(all, moving, column, before, (todo) => starting(todo, start, zone));
}

/**
 * A drop on a tab of "Unscheduled" (Store.drop(_:onBand:before:)): to the Inbox (out of its project or area, as Move
 * sends it), Anytime or Someday, before `before` or at the end.
 */
export function droppedOnBand(lists: Lists, all: readonly Todo[], moving: readonly Todo[], tab: WeekBand, before: string | undefined, zone: string): Todo[] {
  const ids = new Set(moving.map((todo) => todo.id));
  const column = band(lists, tab).flatMap((section) => section.todos).filter((todo) => !ids.has(todo.id));
  return placed(all, moving, column, before, (todo) => {
    if (tab !== "inbox") return starting(todo, { kind: tab }, zone);
    const next = starting(todo, { kind: "inbox" }, zone);
    if (next.projectID !== undefined) delete next.sectionID;
    delete next.projectID;
    delete next.areaID;
    return next;
  });
}

/** The to-dos changed and ordered among `column` (the place without them): before the target, or after the last. */
function placed(all: readonly Todo[], moving: readonly Todo[], column: readonly Todo[], before: string | undefined, change: (todo: Todo) => Todo): Todo[] {
  if (moving.length === 0) return [];
  const movingOrders = new Set(moving.map((todo) => todo.order));
  const orders = all.map((todo) => todo.order).filter((order) => !movingOrders.has(order));
  const target = before === undefined ? undefined : column.find((todo) => todo.id === before);
  const last = column[column.length - 1];
  let slots: number[];
  if (target !== undefined) {
    const below = orders.filter((order) => order < target.order);
    const previous = below.length > 0 ? Math.max(...below) : target.order - (moving.length + 1);
    slots = between(previous, target.order, moving.length);
  } else if (last !== undefined) {
    const above = orders.filter((order) => order > last.order);
    const next = above.length > 0 ? Math.min(...above) : last.order + moving.length + 1;
    slots = between(last.order, next, moving.length);
  } else {
    slots = moving.map((todo) => todo.order);
  }
  return moving.map((todo, index) => ({ ...change(todo), order: slots[index] ?? todo.order }));
}

/** `count` orders spread evenly between two. */
function between(low: number, high: number, count: number): number[] {
  const step = (high - low) / (count + 1);
  return Array.from({ length: count }, (_, index) => low + step * (index + 1));
}
